// Placement: drivers off the roster and whose reason it is, what each day pays,
// the cars with room and the nearest to each driver, relief shifts offered,
// moving notices, applicants held for distance, and new drivers signed but not
// yet placed. db/migrations/8800 to 8803, docs/PLACEMENT-AND-LAYOFF.md.
//
// Whose reason decides money: ours pays the base for the first 30 days and then
// the lay-off allowance; his own is not paid. So the cause is asked for in
// words a manager would use, and every step and relief shift is recorded here,
// because the driver's weekly lay-off letter is built from them.
//
// Every figure and refusal on this page is Postgres's. CLAUDE.md rule 4.
import { useCallback, useEffect, useState } from 'react'
import { Copy, MapPinned, UserRound } from 'lucide-react'
import {
  useAnswerMoveNotice, useAnswerReliefOffer, useGetPlacement, useOpenDriverMovingLink, useRecordMoveNotice,
  useRecordPlacementStep, useRecordReliefOffer, useReleaseHiringHold, useSetBenchCause,
} from '../hooks/backend/placement'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Textarea } from '../lib/shadcn/textarea'
import { toast } from '../lib/shadcn/sonner'
import { Chip } from '../components/interviews/Chip'
import { ErrorNote, PageGate } from '../components/payments/requestParts'
import { formatDay, formatWhen } from '../utils/loans'

type Cause = 'fleetly' | 'driver' | 'suspension' | 'leave' | 'leaving'
type Nearest = { plate: string; minutes: number; open_seats: number }
type Benched = {
  bench_id: string; driver_id: string; driver_name: string; cause: Cause | null; reason: string | null
  since: string; day: number; rule_today: string | null; allowance_from: string | null; last_step: string | null; nearest: Nearest[]
}
type PlacementData = {
  benched: Benched[]
  held_applicants: { application_id: string; name: string; held_at: string; reason: string; nearest_minutes: number | null }[]
  moves: { id: string; driver_id: string; driver_name: string; new_address: string; move_date: string | null; answer_due: string; nearest_minutes: number | null; within_travel: boolean | null }[]
  signed_not_placed: { driver_id: string; driver_name: string; signed_on: string; lapses_on: string }[]
  gaps: { vehicle_id: string; plate: string; seated: number; open_seats: number }[]
  policy: { full_pay_days: number; layoff_allowance_hours: number; travel_minutes: number; relief_notice_hours: number; contract_lapse_days: number }
  switches: { daily_list: boolean; layoff_letters: boolean }
  relief_offers: { id: string; driver_id: string; driver_name: string; shift_date: string; shift_window: string; plate: string | null; covering: string | null; offered_at: string; answer: string | null; decline_reason: string | null; decline_valid: boolean | null }[]
  steps: { bench_id: string; step: string; taken_at: string; by: string | null }[]
  pay_this_period: { driver_id: string; driver_name: string; rule: string; days: number; hours: number | null }[]
  vehicles: { vehicle_id: string; plate: string }[]
  drivers: { driver_id: string; name: string }[]
}

const CAUSE_LABEL: Record<Cause, string> = {
  fleetly: 'Ours: no car, no partner, car off the road',
  driver: 'His own reason',
  suspension: 'Suspended',
  leave: 'On leave',
  leaving: 'Resigning or leaving',
}
const RULE_LABEL: Record<string, string> = {
  full_base: 'Full base pay', layoff_allowance: 'Lay-off allowance', unpaid_driver_reason: 'Not paid, his reason',
  declined_relief: 'Not paid, relief declined', off_day: 'Rest day', suspension: 'Suspension terms', leave: 'Leave',
  leaving: 'Exit process', cause_missing: 'Cannot pay: say whose reason',
}
const RULE_TONE: Record<string, 'green' | 'red' | 'amber' | 'neutral' | 'brand'> = {
  full_base: 'green', layoff_allowance: 'amber', unpaid_driver_reason: 'red', declined_relief: 'red', cause_missing: 'red',
}

function errText(e: unknown) { return e instanceof Error ? e.message : String(e) }

export default function Placement() {
  const page = useGetPlacement<PlacementData | null>()
  const [settled, setSettled] = useState(false)
  const load = page.trigger
  const refresh = useCallback(() => {
    void load({}, { skipCache: true }).catch(() => undefined).finally(() => setSettled(true))
  }, [load])
  useEffect(() => { refresh() }, [refresh])
  const d = page.data ?? null

  const [causeFor, setCauseFor] = useState<Benched | null>(null)
  const [stepFor, setStepFor] = useState<Benched | null>(null)
  const [reliefFor, setReliefFor] = useState<Benched | null>(null)
  const [moveFor, setMoveFor] = useState<{ driver_id: string; driver_name: string } | null>(null)
  const link = useOpenDriverMovingLink<{ url: string }>()

  async function copyLink(driverId: string, name: string) {
    try {
      const r = await link.trigger({ driver_id: driverId }).result
      await navigator.clipboard.writeText(r.url)
      toast.success('Moving link copied', { description: `Send it to ${name} on WhatsApp. It opens his own moving form.` })
    } catch (e) { toast.error('No link', { description: errText(e) }) }
  }

  return (
    <PageGate
      loading={!settled}
      error={d === null ? page.error : null}
      denied={settled && d === null && !page.error && !page.loading}
      onRetry={refresh}
      deniedTitle="Placement is open to every cockpit login."
      deniedHint="If you see this, your login is not linked to a person in cockpit access. Ask an admin."
    >
      <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <MapPinned className="h-5 w-5" />
          </div>
          <div className="min-w-[12rem] flex-1">
            <h1 className="text-lg font-semibold text-foreground">Placement</h1>
            <p className="text-xs text-muted-foreground">
              Who is waiting for a car, and why. Ours pays full base for {d?.policy.full_pay_days ?? 30} days, then {d?.policy.layoff_allowance_hours ?? 4} hours a rostered shift on temporary lay-off. His own reason is not paid.
            </p>
          </div>
          {d && (
            <div className="flex flex-wrap gap-2">
              <Chip tone={d.switches.daily_list ? 'green' : 'neutral'}>Daily list email {d.switches.daily_list ? 'on' : 'off'}</Chip>
              <Chip tone={d.switches.layoff_letters ? 'green' : 'neutral'}>Lay-off letters {d.switches.layoff_letters ? 'on' : 'off'}</Chip>
            </div>
          )}
        </div>

        {d && (
          <div className="space-y-6 p-4">
            <Section title={`Off the roster (${d.benched.length})`} hint="Benched from the Fleet page. Say whose reason it is, record what you do to place him, and offer relief shifts he can reach.">
              {d.benched.length === 0 ? <Empty>Nobody is off the roster.</Empty> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-muted-foreground">
                      <th className="py-1 pr-3">Driver</th><th className="py-1 pr-3">Whose reason</th><th className="py-1 pr-3">Today</th>
                      <th className="py-1 pr-3">Nearest cars with room</th><th className="py-1"></th>
                    </tr></thead>
                    <tbody>
                      {d.benched.map((b) => (
                        <tr key={b.bench_id} className="border-t border-border align-top">
                          <td className="py-2 pr-3">
                            <div className="font-medium">{b.driver_name}</div>
                            <div className="text-xs text-muted-foreground">Since {formatDay(b.since)}, day {b.day}</div>
                          </td>
                          <td className="py-2 pr-3 max-w-[18rem]">
                            {b.cause ? <div>{CAUSE_LABEL[b.cause]}</div> : <Chip tone="red">Not said</Chip>}
                            {b.reason && <div className="text-xs text-muted-foreground">{b.reason}</div>}
                            {b.cause === 'fleetly' && b.allowance_from && <div className="text-xs text-muted-foreground">Allowance from {formatDay(b.allowance_from)}</div>}
                          </td>
                          <td className="py-2 pr-3">
                            {b.rule_today && <Chip tone={RULE_TONE[b.rule_today] ?? 'neutral'}>{RULE_LABEL[b.rule_today] ?? b.rule_today}</Chip>}
                          </td>
                          <td className="py-2 pr-3 text-xs">
                            {b.nearest.length ? b.nearest.map((n) => <div key={n.plate}>{n.plate}: {n.minutes} min, {n.open_seats} open</div>) : <span className="text-muted-foreground">None measured</span>}
                          </td>
                          <td className="py-2">
                            <div className="flex flex-wrap justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => setCauseFor(b)}>Whose reason</Button>
                              <Button size="sm" variant="outline" onClick={() => setStepFor(b)}>Record a step</Button>
                              {b.cause === 'fleetly' && <Button size="sm" variant="outline" onClick={() => setReliefFor(b)}>Offer relief shift</Button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <Section title="Pay this period, days off the roster" hint="From placement_pay_for, the one function payroll reads. Rest days are left out.">
              {d.pay_this_period.length === 0 ? <Empty>No days off the roster this period.</Empty> : (
                <div className="flex flex-wrap gap-2">
                  {d.pay_this_period.map((p) => (
                    <Chip key={p.driver_id + p.rule} tone={RULE_TONE[p.rule] ?? 'neutral'}>
                      {p.driver_name}: {RULE_LABEL[p.rule] ?? p.rule}, {p.days} day{p.days === 1 ? '' : 's'}{p.hours != null ? `, ${p.hours} hours` : ''}
                    </Chip>
                  ))}
                </div>
              )}
            </Section>

            <Section title={`Relief shifts offered, last 30 days (${d.relief_offers.length})`} hint={`A relief shift needs ${d.policy.relief_notice_hours} hours notice. Worked is paid in full; turned down without a valid reason is not paid.`}>
              {d.relief_offers.length === 0 ? <Empty>None offered.</Empty> : d.relief_offers.map((o) => <ReliefRow key={o.id} o={o} />)}
            </Section>

            <Section title={`Moving house, answer due (${d.moves.length})`} hint="Answer within 7 days. If we cannot place him from the new address, his shifts from the move are not paid, so say what was considered.">
              {d.moves.length === 0 ? <Empty>No moving notice waiting.</Empty> : d.moves.map((m) => <MoveRow key={m.id} m={m} />)}
              <div className="mt-2 flex flex-wrap gap-2">
                <DriverPicker data={d} label="Copy a driver's moving link" onPick={(id, name) => void copyLink(id, name)} />
                <DriverPicker data={d} label="Record a move he told you about" onPick={(id, name) => setMoveFor({ driver_id: id, driver_name: name })} />
              </div>
            </Section>

            <Section title={`Signed v7, not yet placed (${d.signed_not_placed.length})`} hint={`Employment begins on his first rostered shift. Without a place in ${d.policy.contract_lapse_days} days the agreement lapses.`}>
              {d.signed_not_placed.length === 0 ? <Empty>Nobody waiting.</Empty> : d.signed_not_placed.map((s) => (
                <div key={s.driver_id} className="text-sm">{s.driver_name}: signed {formatDay(s.signed_on)}, lapses {formatDay(s.lapses_on)}</div>
              ))}
            </Section>

            <Section title={`Applicants held for distance (${d.held_applicants.length})`} hint={`No car with room within ${d.policy.travel_minutes} minutes of his home, so no contract was sent. Released by itself when a car with room comes within reach.`}>
              {d.held_applicants.length === 0 ? <Empty>Nobody held.</Empty> : d.held_applicants.map((h) => <HoldRow key={h.application_id} h={h} />)}
            </Section>

            <Section title={`Cars with room (${d.gaps.length})`} hint="From the published allocation. Cars off the road are left out.">
              <div className="flex flex-wrap gap-2">
                {d.gaps.map((g) => <Chip key={g.vehicle_id} tone={g.seated === 0 ? 'amber' : 'neutral'}>{g.plate}: {g.seated} seated, {g.open_seats} open</Chip>)}
              </div>
            </Section>
          </div>
        )}
      </div>

      {causeFor && <CauseDialog b={causeFor} onClose={() => setCauseFor(null)} />}
      {stepFor && <StepDialog b={stepFor} steps={(d?.steps ?? []).filter((s) => s.bench_id === stepFor.bench_id)} onClose={() => setStepFor(null)} />}
      {reliefFor && d && <ReliefDialog b={reliefFor} vehicles={d.vehicles} onClose={() => setReliefFor(null)} />}
      {moveFor && <MoveDialog who={moveFor} onClose={() => setMoveFor(null)} />}
    </PageGate>
  )
}

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <p className="mb-2 text-xs text-muted-foreground">{hint}</p>
      {children}
    </section>
  )
}
function Empty({ children }: { children: React.ReactNode }) { return <p className="text-sm text-muted-foreground">{children}</p> }

function DriverPicker({ data, label, onPick }: { data: PlacementData; label: string; onPick: (id: string, name: string) => void }) {
  const [open, setOpen] = useState(false)
  const [id, setId] = useState('')
  const name = data.drivers.find((x) => x.driver_id === id)?.name ?? 'the driver'
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        {label.startsWith('Copy') ? <Copy className="mr-1 h-3.5 w-3.5" /> : <UserRound className="mr-1 h-3.5 w-3.5" />}{label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{label}</DialogTitle><DialogDescription>Choose the driver.</DialogDescription></DialogHeader>
          <NativeSelect value={id} onChange={(e) => setId(e.target.value)}>
            <option value="">Choose an active driver</option>
            {data.drivers.map((x) => <option key={x.driver_id} value={x.driver_id}>{x.name}</option>)}
          </NativeSelect>
          <div className="flex justify-end"><Button variant="brand" disabled={!id} onClick={() => { onPick(id, name); setOpen(false) }}>Continue</Button></div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function CauseDialog({ b, onClose }: { b: Benched; onClose: () => void }) {
  const set = useSetBenchCause()
  const [cause, setCause] = useState<Cause | ''>(b.cause ?? '')
  const [note, setNote] = useState('')
  async function save() {
    try { await set.trigger({ bench_id: b.bench_id, cause, note }).result; toast.success('Saved'); onClose() }
    catch (e) { toast.error('Not saved', { description: errText(e) }) }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Whose reason is {b.driver_name} off?</DialogTitle>
          <DialogDescription>This decides his pay. Ours pays full base, then the lay-off allowance. His own is not paid, so write it as you would say it to him.</DialogDescription></DialogHeader>
        <NativeSelect value={cause} onChange={(e) => setCause(e.target.value as Cause)}>
          <option value="">Choose</option>
          {(Object.keys(CAUSE_LABEL) as Cause[]).map((c) => <option key={c} value={c}>{CAUSE_LABEL[c]}</option>)}
        </NativeSelect>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened, in a sentence he could be shown" />
        {set.error && <ErrorNote>{set.error}</ErrorNote>}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="brand" disabled={!cause || set.loading} onClick={() => void save()}>Save</Button></div>
      </DialogContent>
    </Dialog>
  )
}

function StepDialog({ b, steps, onClose }: { b: Benched; steps: PlacementData['steps']; onClose: () => void }) {
  const rec = useRecordPlacementStep()
  const [step, setStep] = useState('')
  async function save() {
    try { await rec.trigger({ bench_id: b.bench_id, step }).result; toast.success('Step recorded'); onClose() }
    catch (e) { toast.error('Not recorded', { description: errText(e) }) }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>What was done to place {b.driver_name}?</DialogTitle>
          <DialogDescription>His weekly lay-off letter lists these, so write it for him to read.</DialogDescription></DialogHeader>
        <Textarea rows={3} value={step} onChange={(e) => setStep(e.target.value)} placeholder="For example: asked the owner of MZ92RBGP to bring the car back on Monday" />
        {steps.length > 0 && <div className="space-y-1 text-xs text-muted-foreground">{steps.map((s) => <div key={s.taken_at}>{formatWhen(s.taken_at)}: {s.step}{s.by ? `, ${s.by}` : ''}</div>)}</div>}
        {rec.error && <ErrorNote>{rec.error}</ErrorNote>}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="brand" disabled={!step.trim() || rec.loading} onClick={() => void save()}>Record</Button></div>
      </DialogContent>
    </Dialog>
  )
}

function ReliefDialog({ b, vehicles, onClose }: { b: Benched; vehicles: PlacementData['vehicles']; onClose: () => void }) {
  const rec = useRecordReliefOffer()
  const [date, setDate] = useState('')
  const [win, setWin] = useState('day')
  const [vehicle, setVehicle] = useState('')
  const [covering, setCovering] = useState('')
  async function save() {
    try { await rec.trigger({ driver_id: b.driver_id, shift_date: date, shift_window: win, vehicle_id: vehicle || null, covering }).result; toast.success('Offer recorded'); onClose() }
    catch (e) { toast.error('Not recorded', { description: errText(e) }) }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Offer {b.driver_name} a relief shift</DialogTitle>
          <DialogDescription>Record it when you offer it. Record his answer below once he gives it.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <NativeSelect value={win} onChange={(e) => setWin(e.target.value)}><option value="day">Day shift, 04:00</option><option value="night">Night shift, 16:00</option></NativeSelect>
        </div>
        <NativeSelect value={vehicle} onChange={(e) => setVehicle(e.target.value)}>
          <option value="">Which car</option>{vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.plate}</option>)}
        </NativeSelect>
        <Input value={covering} onChange={(e) => setCovering(e.target.value)} placeholder="Covering for whom, for example leave or sick day" />
        {rec.error && <ErrorNote>{rec.error}</ErrorNote>}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="brand" disabled={!date || rec.loading} onClick={() => void save()}>Record the offer</Button></div>
      </DialogContent>
    </Dialog>
  )
}

function ReliefRow({ o }: { o: PlacementData['relief_offers'][number] }) {
  const ans = useAnswerReliefOffer()
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')
  const [valid, setValid] = useState('')
  async function answer(a: string) {
    try {
      await ans.trigger({ id: o.id, answer: a, decline_reason: reason, decline_valid: valid === '' ? null : valid === 'yes' }).result
      toast.success('Answer recorded'); setDeclining(false)
    } catch (e) { toast.error('Not recorded', { description: errText(e) }) }
  }
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border py-2 text-sm">
      <span className="font-medium">{o.driver_name}</span>
      <span className="text-muted-foreground">{o.shift_window} shift {formatDay(o.shift_date)}{o.plate ? ` on ${o.plate}` : ''}{o.covering ? `, covering ${o.covering}` : ''}</span>
      {o.answer ? <Chip tone={o.answer === 'accepted' ? 'green' : o.answer === 'declined' ? (o.decline_valid ? 'amber' : 'red') : 'neutral'}>
          {o.answer}{o.answer === 'declined' ? (o.decline_valid ? ', valid reason' : ', no valid reason') : ''}</Chip>
        : declining ? (
          <div className="flex w-full flex-wrap gap-2">
            <Input className="h-8 flex-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="The reason he gave" />
            <NativeSelect className="h-8 w-56" value={valid} onChange={(e) => setValid(e.target.value)}>
              <option value="">Is it a valid reason?</option><option value="yes">Valid (clause 6.8): still paid</option><option value="no">Not valid: shift not paid</option>
            </NativeSelect>
            <Button size="sm" variant="brand" disabled={!reason.trim() || !valid || ans.loading} onClick={() => void answer('declined')}>Save</Button>
          </div>
        ) : (
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="outline" onClick={() => void answer('accepted')}>Accepted</Button>
            <Button size="sm" variant="outline" onClick={() => setDeclining(true)}>Turned down</Button>
            <Button size="sm" variant="outline" onClick={() => void answer('no_answer')}>No answer</Button>
            <Button size="sm" variant="outline" onClick={() => void answer('withdrawn')}>Withdrawn</Button>
          </div>
        )}
    </div>
  )
}

function MoveRow({ m }: { m: PlacementData['moves'][number] }) {
  const ans = useAnswerMoveNotice()
  const [note, setNote] = useState('')
  async function answer(a: string) {
    try { await ans.trigger({ id: m.id, answer: a, note }).result; toast.success('Answered') }
    catch (e) { toast.error('Not answered', { description: errText(e) }) }
  }
  return (
    <div className="space-y-1 border-t border-border py-2 text-sm">
      <div><span className="font-medium">{m.driver_name}</span> to {m.new_address}{m.move_date ? `, moving ${formatDay(m.move_date)}` : ''}. Answer by {formatDay(m.answer_due)}.</div>
      <div className="text-xs text-muted-foreground">
        {m.nearest_minutes == null ? 'Not measured yet, or the address was too vague to measure.' : `Nearest partner on a car with room: ${m.nearest_minutes} minutes${m.within_travel ? ', within reach' : ', out of reach'}.`}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input className="h-8 flex-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Cars and patterns considered" />
        <Button size="sm" variant="outline" disabled={ans.loading} onClick={() => void answer('can_place')}>We can place him</Button>
        <Button size="sm" variant="outline" disabled={ans.loading} onClick={() => void answer('cannot_place')}>We cannot place him</Button>
      </div>
    </div>
  )
}

function HoldRow({ h }: { h: PlacementData['held_applicants'][number] }) {
  const rel = useReleaseHiringHold()
  const [reason, setReason] = useState('')
  async function release() {
    try { await rel.trigger({ application_id: h.application_id, reason }).result; toast.success('Released. The contract goes on the next send.') }
    catch (e) { toast.error('Not released', { description: errText(e) }) }
  }
  return (
    <div className="space-y-1 border-t border-border py-2 text-sm">
      <div><span className="font-medium">{h.name}</span>, held {formatWhen(h.held_at)}. {h.reason}</div>
      <div className="flex gap-2">
        <Input className="h-8 flex-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why he can be placed after all" />
        <Button size="sm" variant="outline" disabled={!reason.trim() || rel.loading} onClick={() => void release()}>Release</Button>
      </div>
    </div>
  )
}

function MoveDialog({ who, onClose }: { who: { driver_id: string; driver_name: string }; onClose: () => void }) {
  const rec = useRecordMoveNotice()
  const [addr, setAddr] = useState('')
  const [date, setDate] = useState('')
  async function save() {
    try { await rec.trigger({ driver_id: who.driver_id, new_address: addr, move_date: date }).result; toast.success('Recorded. The new address is being measured.'); onClose() }
    catch (e) { toast.error('Not recorded', { description: errText(e) }) }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{who.driver_name} is moving</DialogTitle>
          <DialogDescription>The full new address, so the drive to a partner can be measured.</DialogDescription></DialogHeader>
        <Textarea rows={3} value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="House number, street, suburb, town" />
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        {rec.error && <ErrorNote>{rec.error}</ErrorNote>}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="brand" disabled={addr.trim().length < 10 || rec.loading} onClick={() => void save()}>Record</Button></div>
      </DialogContent>
    </Dialog>
  )
}
